import { Fragment } from 'react';
import { Check } from 'lucide-react';
import type { JobStatus } from '@core/enums';
import {
  getJobOpsFlowPhaseIndex,
  isJobOpsFlowOffHappyPath,
  JOB_OPS_FLOW_STEPS,
  JOB_OPS_STATUS_LABELS,
} from '@core/constants/jobLifecycle';

type Orientation = 'horizontal' | 'vertical';

type Props = { orientation: Orientation; status: JobStatus; actionHint?: string };

function HeartbeatNode({ accent }: { accent: 'emerald' | 'amber' }) {
  const ring =
    accent === 'amber'
      ? 'border-amber-400 shadow-[0_0_0_3px_rgba(251,191,36,0.22)]'
      : 'border-emerald-400 shadow-[0_0_0_3px_rgba(52,211,153,0.2)]';
  const dot = accent === 'amber' ? 'bg-amber-400' : 'bg-emerald-400';
  const glow = accent === 'amber' ? 'bg-amber-400/30' : 'bg-emerald-400/30';
  return (
    <div className="relative flex h-10 w-10 shrink-0 items-center justify-center" aria-current="step">
      <span className={`absolute inset-[-2px] rounded-full ${glow} animate-heartbeat`} aria-hidden />
      <span
        className={`relative flex h-10 w-10 items-center justify-center rounded-full border-2 ${ring} bg-gray-950`}
      >
        <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
      </span>
    </div>
  );
}

function CompletedNode() {
  return (
    <div
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white shadow-sm"
      aria-hidden
    >
      <Check className="h-5 w-5" strokeWidth={2.5} />
    </div>
  );
}

function PendingNode({ n }: { n: number }) {
  return (
    <div
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-[var(--vscode-border-primary)] bg-[color-mix(in_srgb,var(--vscode-bg-tertiary)_85%,transparent)] text-sm font-semibold vscode-text-tertiary"
      aria-hidden
    >
      {n}
    </div>
  );
}

function segmentComplete(segmentStartIndex: number, phaseIndex: number, offPath: boolean): boolean {
  if (offPath) return false;
  return segmentStartIndex < phaseIndex;
}

function bracketStepNavLabel(s: string) {
  const t = s.trim();
  if (t.startsWith('[')) return s;
  return `[${t}]`;
}

export function JobOpsFlowStepper({ orientation, status, actionHint }: Props) {
  const hint =
    actionHint ??
    'Current stage — change ops status under Job details when ready.';
  const steps = JOB_OPS_FLOW_STEPS;
  const last = steps.length - 1;
  const phaseIndex = getJobOpsFlowPhaseIndex(status);
  const offPath = isJobOpsFlowOffHappyPath(status);
  const onHold = status === 'on_hold';
  const accent: 'emerald' | 'amber' = onHold ? 'amber' : 'emerald';

  const stepDone = (i: number) => {
    if (offPath) return false;
    if (status === 'filled') return true;
    if (phaseIndex > i) return true;
    return false;
  };

  const stepActive = (i: number) => {
    if (offPath) return false;
    if (status === 'filled') return false;
    return phaseIndex === i;
  };

  const stepTitle = (i: number, base: string) => {
    if (i === 1 && status === 'filled') return JOB_OPS_STATUS_LABELS.filled;
    return base;
  };

  const stepDescription = (i: number, base: string) => {
    if (i === 1 && status === 'filled') {
      return 'Hire complete — ops status is terminal until you reopen from edit if needed.';
    }
    return base;
  };

  if (orientation === 'horizontal') {
    return (
      <div className="rounded-xl border vscode-border bg-[color-mix(in_srgb,var(--vscode-bg-secondary)_92%,transparent)] p-4 sm:p-5">
        <p className="text-xs font-medium uppercase tracking-wider vscode-text-tertiary mb-4">Job ops lifecycle</p>
        <div className="overflow-x-auto pb-1 -mx-1 px-1">
          <div className="flex min-w-[320px] items-center sm:min-w-0">
            {steps.map((step, i) => {
              const done = stepDone(i);
              const active = stepActive(i);
              const segDone = segmentComplete(i, phaseIndex, offPath);

              return (
                <Fragment key={step.key}>
                  <div className="flex min-w-[120px] flex-1 flex-col items-center text-center px-1">
                    {done ? (
                      <CompletedNode />
                    ) : active ? (
                      <HeartbeatNode accent={accent} />
                    ) : (
                      <PendingNode n={i + 1} />
                    )}
                    <p className="mt-2 text-xs font-semibold vscode-text-primary leading-tight">
                      {bracketStepNavLabel(stepTitle(i, step.title))}
                    </p>
                    <p className="mt-1 text-[11px] leading-snug vscode-text-secondary line-clamp-4">
                      {stepDescription(i, step.description)}
                    </p>
                  </div>
                  {i < last ? (
                    <div
                      className={`mx-0.5 h-0.5 w-5 shrink-0 rounded-full sm:w-10 md:flex-1 md:max-w-[56px] ${
                        segDone ? 'bg-emerald-500' : 'bg-gray-700'
                      }`}
                      aria-hidden
                    />
                  ) : null}
                </Fragment>
              );
            })}
          </div>
        </div>
        {offPath ? (
          <p className="text-xs text-amber-200/80 mt-4 border-t border-amber-900/30 pt-3">
            Status is <strong className="text-amber-100">{JOB_OPS_STATUS_LABELS.closed}</strong> — this job is off the
            active recruiting path. Reopen to <strong className="text-amber-100">{JOB_OPS_STATUS_LABELS.open}</strong>{' '}
            from <strong className="text-amber-100">Job details</strong> if the role is live again.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="rounded-xl border vscode-border bg-gray-950/40 p-4">
      <p className="text-xs font-medium uppercase tracking-wider vscode-text-tertiary mb-4">Where you are (ops)</p>
      <ul className="space-y-0">
        {steps.map((step, i) => {
          const done = stepDone(i);
          const active = stepActive(i);
          const lineDone = segmentComplete(i, phaseIndex, offPath);

          return (
            <li key={step.key} className="flex gap-4">
              <div className="flex w-10 shrink-0 flex-col items-center">
                {done ? (
                  <CompletedNode />
                ) : active ? (
                  <HeartbeatNode accent={accent} />
                ) : (
                  <PendingNode n={i + 1} />
                )}
                {i < last ? (
                  <div
                    className={`my-1 w-0.5 flex-1 min-h-10 rounded-full ${
                      lineDone ? 'bg-emerald-500' : 'bg-gray-700'
                    }`}
                    aria-hidden
                  />
                ) : null}
              </div>
              <div className={`min-w-0 flex-1 ${i < last ? 'pb-6' : ''}`}>
                <p
                  className={`text-sm font-semibold ${
                    active ? 'vscode-text-primary' : done ? 'text-emerald-100/90' : 'vscode-text-secondary'
                  }`}
                >
                  {bracketStepNavLabel(stepTitle(i, step.title))}
                </p>
                <p className="text-xs vscode-text-secondary mt-1 leading-relaxed">{stepDescription(i, step.description)}</p>
                {active && !offPath ? (
                  <p className="text-[11px] text-brand-400/90 mt-2 font-medium">{hint}</p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {offPath ? (
        <p className="text-xs text-amber-200/80 mt-4 border-t border-amber-900/30 pt-3">
          Closed — reopen to Open from job edit if this role should be active again.
        </p>
      ) : null}
    </div>
  );
}
